import {
  ACTIVITY_STATUS,
  convertDateToYYYYMMDDFormat,
  convertEventSelectToTarget,
  CustomDateRangePicker,
  FormFeedBack,
  NoDataComponent,
  PageWrapper,
  Select,
  STATUSPAGE,
  TableVirtualized,
  TableWithGroupingsLoadingSkeleton,
} from '@repo/ui';
import { ActivityReportInternshipLocationUseCaseInterface } from 'domain/usecases';
import { useFormik } from 'formik';
import noData from 'presentation/app/assets/images/no-task.png';
import { notifyError } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  clearLoadActivityReportInternship,
  loadActivityReportInternship,
} from 'presentation/config/store/reports/activity-report-internship';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Col,
  Card,
  Input,
  Label,
  Spinner,
  FormGroup,
  CardBody,
  Row,
} from 'reactstrap';
import { v4 as uuid } from 'uuid';
import * as Yup from 'yup';

import { makeInternshipLocationActivityReportColumns } from '../../../../common/columns/activities/internship-location-activity-report-columns';
import { ReportsGeneralFilters } from '../../../../components/reports-general-filters';

const InternshipLocationActivityReport = () => {
  const { getActivityReportInternshipInterface } = useService();

  const [buttonSelectedSubmit, setButtonSelectedSubmit] =
    useState<string>(null);
  const [hasFetchedData, setHasFetchedData] = useState(false);
  const [statusGet, setStatusGet] = useState<STATUSPAGE>();

  const dispatch = useDispatch();

  const { activityReportInternship: activityReportInternshipResponse } =
    useAppSelector((state) => state.activityReportInternship);

  const todaysDate = new Date();

  // Methods
  const requestActivityReportInternshipLocation = useCallback(
    async (params: ActivityReportInternshipLocationUseCaseInterface.Params) => {
      setStatusGet(STATUSPAGE.LOADING);
      await getActivityReportInternshipInterface
        .getActivityReportInternshipInterface(params)
        .then((res: any) => {
          let blob = new Blob([res], {
            type: `application/${params?.type === 0 ? 'pdf' : 'vnd.openxmlformats-officedocument.spreadsheetml.sheet'}`,
          });
          let link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          window.open(link.href, '_blank');
        })
        .catch((err) => {
          notifyError(err.message);
          console.log(err);
        })
        .finally(() => {
          setStatusGet(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestActivityReportInternshipLocationJson = useCallback(
    async (params: ActivityReportInternshipLocationUseCaseInterface.Params) => {
      setStatusGet(STATUSPAGE.LOADING);
      await getActivityReportInternshipInterface
        .getActivityReportInternshipInterface(params)
        .then((res: any) => {
          dispatch(loadActivityReportInternship(res));
        })
        .catch((err) => {
          dispatch(clearLoadActivityReportInternship());
          console.log(err);
          notifyError(err.message);
        })
        .finally(() => {
          setStatusGet(STATUSPAGE.DONE);
          setHasFetchedData(true);
        });
    },
    [],
  );

  const firstDayOfMonth = new Date(
    todaysDate.getFullYear(),
    todaysDate.getMonth(),
    1,
  );

  const formattedData = activityReportInternshipResponse?.reduce(
    (acc, internshiplocation) => [
      ...acc,
      {
        id: uuid(),
        isHeader: true,
        title: (
          <div className="d-flex justify-content-between">
            <span>{internshiplocation?.place}</span>{' '}
            <span>{internshiplocation?.course}</span>
          </div>
        ),
      },
      ...internshiplocation?.activities?.map((activity) => ({
        id: uuid(),
        ...activity,
        hasFooter: !!activity?.feedback,
        footerText: activity?.feedback
          ? `Feedback: ${activity?.feedback}`
          : null,
      })),
    ],
    [],
  );

  const validationInternshipLocationActivityReport = useFormik({
    enableReinitialize: true,

    initialValues: {
      unit: null,
      starts: convertDateToYYYYMMDDFormat(firstDayOfMonth),
      ends: convertDateToYYYYMMDDFormat(todaysDate),
      entity: null,
      activities: [],
      course: null,
      groups: [],
      students: [],
      semester: {
        value: null,
      },
      validation: null,
      activityStatus: [],
    },
    validationSchema: Yup.object({
      entity: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .required('Organização é obrigatória'),
      unit: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      groups: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .nullable(),
      ),
      activities: Yup.array().of(
        Yup.object().shape({
          label: Yup.string(),
          value: Yup.string(),
        }),
      ),
      course: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      students: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .nullable(),
      ),
      activityStatus: Yup.array().of(
        Yup.object().shape({
          label: Yup.string(),
          value: Yup.string(),
        }),
      ),
      semester: Yup.object()
        .shape({
          label: Yup.string().nullable(),
          value: Yup.string().nullable(),
        })
        .nullable(),
      validation: Yup.string().nullable(),
      starts: Yup.date().nullable().required('Data inicial é obrigatório'),
      ends: Yup.date().nullable().required('Data final é obrigatório'),
    }),

    onSubmit: (values) => {
      const groupsValues = values?.groups?.map((group) => group?.value);
      const activityStatusValues = values?.activityStatus?.map(
        (activityStatus) => activityStatus?.value,
      );
      {
        buttonSelectedSubmit === 'search-button-json' &&
          requestActivityReportInternshipLocationJson({
            ends: values?.ends,
            groups: groupsValues,
            entity: values?.entity?.value,
            starts: values?.starts,
            course: values?.course?.value,
            type: 2,
            curricularUnit: values?.unit?.value,
            validation: values?.validation,
            semester: values?.semester?.value,
            activityStatus: activityStatusValues,
          });
      }
      {
        buttonSelectedSubmit === 'export-pdf' &&
          requestActivityReportInternshipLocation({
            ends: values?.ends,
            groups: groupsValues,
            entity: values?.entity?.value,
            starts: values?.starts,
            course: values?.course?.value,
            type: 0,
            curricularUnit: values?.unit?.value,
            validation: values?.validation,
            semester: values?.semester?.value,
            activityStatus: activityStatusValues,
          });
      }
      {
        buttonSelectedSubmit === 'export-excel' &&
          requestActivityReportInternshipLocation({
            ends: values?.ends,
            groups: groupsValues,
            entity: values?.entity?.value,
            starts: values?.starts,
            course: values?.course?.value,
            type: 1,
            curricularUnit: values?.unit?.value,
            validation: values?.validation,
            semester: values?.semester?.value,
            activityStatus: activityStatusValues,
          });
      }
    },
  });

  return (
    <PageWrapper title="Analítico">
      <Col className="mb-3 border-0 d-flex flex-column">
        <h4>Analítico</h4>
        <p className="text-muted mb-0">
          Acompanhe o relatório analítico de atividades por local de estágio.
        </p>
      </Col>

      <Card>
        <ReportsGeneralFilters
          buttonsChildren={
            <div className="d-flex gap-2 justify-content-end align-content-end">
              <button
                id="search-button-json"
                type="submit"
                onClick={() => {
                  setButtonSelectedSubmit('search-button-json');
                }}
                className="btn btn-primary waves-effect waves-light"
                disabled={statusGet === STATUSPAGE.LOADING && true}
              >
                {buttonSelectedSubmit === 'search-button-json' &&
                statusGet === STATUSPAGE.LOADING ? (
                  <Spinner size="sm">{['Carregando...']}</Spinner>
                ) : (
                  <>
                    Visualizar
                    <i className="ri-computer-line mx-2"></i>
                  </>
                )}
              </button>

              <button
                type="submit"
                id="export-pdf"
                onClick={() => {
                  setButtonSelectedSubmit('export-pdf');
                }}
                className="btn btn-light waves-effect waves-light"
                disabled={statusGet === STATUSPAGE.LOADING && true}
              >
                {buttonSelectedSubmit === 'export-pdf' &&
                statusGet === STATUSPAGE.LOADING ? (
                  <Spinner size="sm">{['Carregando...']}</Spinner>
                ) : (
                  <>
                    Visualizar em PDF
                    <i className="las la-file-pdf mx-2"></i>
                  </>
                )}
              </button>
              <button
                id="export-excel"
                type="submit"
                onClick={() => {
                  setButtonSelectedSubmit('export-excel');
                }}
                className="btn btn-light waves-effect waves-light"
                disabled={statusGet === STATUSPAGE.LOADING && true}
              >
                {buttonSelectedSubmit === 'export-excel' &&
                statusGet === STATUSPAGE.LOADING ? (
                  <Spinner size="sm">{['Carregando...']}</Spinner>
                ) : (
                  <>
                    Exportar para Excel
                    <i className="ri-file-excel-2-line mx-2"></i>
                  </>
                )}
              </button>
            </div>
          }
          validation={validationInternshipLocationActivityReport}
          inputsChildren={
            <>
              <Col lg={4} className="mb-3">
                <Label htmlFor="activities" className="form-label">
                  Situação da Atividade:
                </Label>
                <Select
                  isMulti
                  isClearable
                  id="activityStatus"
                  name="activityStatus"
                  placeholder="Situação da Atividade"
                  value={
                    validationInternshipLocationActivityReport.values
                      .activityStatus as any
                  }
                  onChange={(e: any) => {
                    validationInternshipLocationActivityReport.handleChange(
                      convertEventSelectToTarget('activityStatus', e),
                    );
                  }}
                  onBlur={validationInternshipLocationActivityReport.handleBlur}
                  options={[
                    { label: 'Agendadas', value: ACTIVITY_STATUS.SCHEDULED },
                    { label: 'Realizadas', value: ACTIVITY_STATUS.FINISHED },
                    { label: 'Canceladas', value: ACTIVITY_STATUS.CANCELED },
                    { label: 'Perdidas', value: ACTIVITY_STATUS.MISSED },
                    {
                      label: 'Perdidas Justificadas',
                      value: ACTIVITY_STATUS.MISSED_JUSTIFIED,
                    },
                  ]}
                />
                <FormFeedBack
                  error={
                    validationInternshipLocationActivityReport?.errors
                      ?.activityStatus as any
                  }
                  isInvalid={
                    validationInternshipLocationActivityReport?.touched &&
                    (validationInternshipLocationActivityReport?.errors
                      ?.activityStatus as any)
                  }
                />
              </Col>
              <Col sm={12} md={6} lg={3} className="mb-3">
                <Row>
                  <Label htmlFor="initial-date" className="form-label">
                    Período
                  </Label>
                  <CustomDateRangePicker
                    onDateChange={(start, ends) => {
                      validationInternshipLocationActivityReport.setFieldValue(
                        'starts',
                        convertDateToYYYYMMDDFormat(start),
                      );
                      validationInternshipLocationActivityReport.setFieldValue(
                        'ends',
                        convertDateToYYYYMMDDFormat(ends),
                      );
                    }}
                  />
                </Row>
              </Col>

              <Col md={4}>
                <Label htmlFor="activities" className="form-label">
                  Validação:
                </Label>

                <div className="d-flex gap-4">
                  <FormGroup>
                    <Input
                      name="validation"
                      id="all"
                      type="radio"
                      value={null}
                      checked={
                        !validationInternshipLocationActivityReport.values
                          .validation
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          validationInternshipLocationActivityReport.handleChange(
                            {
                              target: {
                                name: 'validation',
                                value: null,
                              },
                            },
                          );
                        }
                      }}
                      className="form-check-input"
                      style={{ cursor: 'pointer' }}
                    />
                    <Label htmlFor="all" className="ms-1 form-check-label">
                      Todos
                    </Label>
                  </FormGroup>
                  <FormGroup>
                    <Input
                      name="validation"
                      id="validated"
                      type="radio"
                      value="1"
                      onChange={
                        validationInternshipLocationActivityReport.handleChange
                      }
                      className="form-check-input"
                      style={{ cursor: 'pointer' }}
                    />
                    <Label
                      htmlFor="validated"
                      className="ms-1 form-check-label"
                    >
                      Validadas
                    </Label>
                  </FormGroup>
                  <FormGroup>
                    <Input
                      name="validation"
                      id="not-validated"
                      type="radio"
                      value="2"
                      onChange={
                        validationInternshipLocationActivityReport.handleChange
                      }
                      className="form-check-input"
                      style={{ cursor: 'pointer' }}
                    />
                    <Label
                      htmlFor="not-validated"
                      className="ms-1 form-check-label"
                      onChange={
                        validationInternshipLocationActivityReport.handleChange
                      }
                    >
                      Rejeitadas
                    </Label>
                  </FormGroup>
                  <FormGroup>
                    <Input
                      name="validation"
                      id="rejected"
                      type="radio"
                      value="3"
                      onChange={
                        validationInternshipLocationActivityReport.handleChange
                      }
                      className="form-check-input"
                      style={{ cursor: 'pointer' }}
                    />
                    <Label htmlFor="rejected" className="ms-1 form-check-label">
                      Pendente
                    </Label>
                  </FormGroup>
                </div>
              </Col>
            </>
          }
        />
        {activityReportInternshipResponse?.length === 0 &&
          hasFetchedData &&
          statusGet === STATUSPAGE.DONE && (
            <div className="p-4">
              <NoDataComponent src={noData} />
            </div>
          )}

        {statusGet === STATUSPAGE.LOADING && (
          <TableWithGroupingsLoadingSkeleton />
        )}

        {statusGet === STATUSPAGE.DONE &&
          activityReportInternshipResponse?.length > 0 && (
            <CardBody>
              <TableVirtualized
                columns={makeInternshipLocationActivityReportColumns()}
                data={formattedData}
              />
            </CardBody>
          )}
      </Card>
    </PageWrapper>
  );
};

export default InternshipLocationActivityReport;
